import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  User as FirebaseUser,
  UserCredential,
} from 'firebase/auth';

import { auth } from './config';
import type { UserProfile } from '@shared/types/user';
import { UserRole } from '@shared/types/user';
import { createUserProfile, getUserProfile } from './firestore';

/**
 * Convert Firebase User to UserProfile
 */
function firebaseUserToProfile(
  firebaseUser: FirebaseUser,
  additionalData?: Partial<UserProfile>
): UserProfile {
  return {
    id: firebaseUser.uid,
    email: firebaseUser.email || '',
    displayName: firebaseUser.displayName,
    phoneNumber: firebaseUser.phoneNumber,
    role: additionalData?.role || UserRole.USER,
    emailVerified: firebaseUser.emailVerified,
    phoneVerified: !!firebaseUser.phoneNumber,
    createdAt: additionalData?.createdAt || new Date(),
    updatedAt: new Date(),
    photoURL: firebaseUser.photoURL,
  };
}

/**
 * Sign in with email and password
 */
export async function signInEmailPassword(
  email: string,
  password: string
): Promise<UserProfile> {
  const userCredential: UserCredential = await signInWithEmailAndPassword(
    auth,
    email,
    password
  );

  // Get user profile from Firestore
  const profile = await getUserProfile(userCredential.user.uid);
  return profile;
}

/**
 * Sign up with email and password
 */
export async function signUpEmailPassword(
  email: string,
  password: string,
  displayName?: string
): Promise<UserProfile> {
  const userCredential: UserCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password
  );

  // Update display name if provided
  if (displayName && userCredential.user) {
    await updateProfile(userCredential.user, { displayName });
  }

  // Create user profile in Firestore
  const profile: UserProfile = firebaseUserToProfile(userCredential.user, {
    displayName,
    createdAt: new Date(),
    updatedAt: new Date(),
  });

  await createUserProfile(profile);

  return profile;
}

/**
 * Sign in with Google OAuth
 */
export async function signInWithGoogle(): Promise<UserProfile> {
  const provider = new GoogleAuthProvider();
  const userCredential: UserCredential = await signInWithPopup(auth, provider);

  // Check if user profile exists, create if not
  let profile: UserProfile;
  try {
    profile = await getUserProfile(userCredential.user.uid);
  } catch {
    // User doesn't exist, create profile
    profile = firebaseUserToProfile(userCredential.user, {
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    await createUserProfile(profile);
  }

  return profile;
}

/**
 * Sign in with Apple OAuth
 */
export async function signInWithApple(): Promise<UserProfile> {
  const provider = new OAuthProvider('apple.com');
  // Set the service ID for Apple Sign-In
  provider.addScope('email');
  provider.addScope('name');
  
  const userCredential: UserCredential = await signInWithPopup(auth, provider);

  // Check if user profile exists, create if not
  let profile: UserProfile;
  try {
    profile = await getUserProfile(userCredential.user.uid);
  } catch {
    // User doesn't exist, create profile
    // Apple may not provide displayName on first sign-in, so we'll use email prefix
    const displayName = userCredential.user.displayName || 
      userCredential.user.email?.split('@')[0] || 
      'Apple User';
    
    profile = firebaseUserToProfile(userCredential.user, {
      displayName,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    await createUserProfile(profile);
  }

  return profile;
}

/**
 * Sign in with phone number (SMS)
 */
export async function signInWithPhone(
  phoneNumber: string,
  recaptchaVerifier: RecaptchaVerifier
): Promise<{ confirmationResult: any }> {
  const confirmationResult = await signInWithPhoneNumber(
    auth,
    phoneNumber,
    recaptchaVerifier
  );
  return { confirmationResult };
}

/**
 * Verify phone code and complete sign in
 */
export async function verifyPhoneCode(
  confirmationResult: any,
  code: string
): Promise<UserProfile> {
  const userCredential = await confirmationResult.confirm(code);

  // Check if user profile exists, create if not
  let profile: UserProfile;
  try {
    profile = await getUserProfile(userCredential.user.uid);
  } catch {
    // User doesn't exist, create profile
    profile = firebaseUserToProfile(userCredential.user, {
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    await createUserProfile(profile);
  }

  return profile;
}

/**
 * Sign out current user
 */
export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

/**
 * Send password reset email
 */
export async function resetPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

/**
 * Update user profile
 */
export async function updateUserProfile(
  updates: Partial<UserProfile>
): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No user signed in');
  }

  // Update Firebase Auth profile
  const authUpdates: { displayName?: string; photoURL?: string | null } = {};
  if (updates.displayName !== undefined) {
    authUpdates.displayName = updates.displayName ?? undefined;
  }
  if (updates.photoURL !== undefined) {
    authUpdates.photoURL = updates.photoURL ?? null;
  }

  if (Object.keys(authUpdates).length > 0) {
    await updateProfile(user, authUpdates);
  }

  // Update Firestore profile
  // This will be handled by Firestore helpers
}

